import { lastSearchHistoryEntries } from '../search/searchHistory/searchHistory.utils'
import { selectSearchHistory, selectLastTenHistoryEntries } from '../search/searchHistory/searchHistory.selectors'
import { createSelector } from 'reselect'
import {
  selectFakeFavorites,
  selectIsUserAccountEnabled,
  selectUserFavorites
} from '../userAccount/userAccount.selectors'
import { isLabelMatchingInputValueKeyword, normalizeInputValueKeyword, normalizeLabel } from './suggest.selectors.utils'
import { SUGGEST_FROM_VALUES } from '../../domain/suggest/suggest.constants'

const addFromProperty = from => item => ({ ...item, from })

const selectSuggestState = store => store?.suggest ?? {}

export const selectSuggestInputValue = createSelector(selectSuggestState, suggest => suggest?.inputValue ?? '')

export const selectIsSuggestInputValueEmpty = createSelector(selectSuggestInputValue, inputValue => inputValue === '')

export const selectSuggestError = createSelector(selectSuggestState, suggest => suggest?.error ?? null)

export const selectServiceSuggestions = createSelector(selectSuggestState, suggest =>
  (suggest?.serviceSuggestions ?? []).map(addFromProperty(SUGGEST_FROM_VALUES.service))
)

export const selectSearchHistorySuggestions = createSelector(
  selectSuggestInputValue,
  selectSearchHistory,
  selectLastTenHistoryEntries,
  (inputValue, searchHistory, lastTenHistoryEntries) => {
    if (inputValue === '') return lastTenHistoryEntries.map(addFromProperty(SUGGEST_FROM_VALUES.history))

    const normalizedInputValue = normalizeInputValueKeyword(inputValue)

    return lastSearchHistoryEntries(normalizedInputValue, inputValue, searchHistory).map(
      addFromProperty(SUGGEST_FROM_VALUES.history)
    )
  }
)

const formatFavoriteForSuggest = ({ uuid, label, address, favIcon, type, incentive, labels }) => {
  return {
    uuid,
    labels: address ? [label, address] : labels,
    subtype: { id: favIcon },
    type,
    from: SUGGEST_FROM_VALUES.favorites,
    incentive: Boolean(incentive)
  }
}

const formatHomeOrWorkFavoriteForSuggest = suggestion => {
  return {
    ...formatFavoriteForSuggest(suggestion),
    address: suggestion.address
  }
}

export const selectSuggestFakeFavorites = createSelector(selectFakeFavorites, fakeFavorites => {
  const { home, work } = fakeFavorites
  return {
    home: {
      from: SUGGEST_FROM_VALUES.favorites,
      ...home
    },
    work: {
      from: SUGGEST_FROM_VALUES.favorites,
      ...work
    }
  }
})

export const selectFavoritesFormattedForSuggest = createSelector(
  selectIsUserAccountEnabled,
  selectUserFavorites,
  selectSuggestFakeFavorites,
  (isUserAccountAvailable, favorites, fakeFavorites) => {
    if (!isUserAccountAvailable) return []

    const { others, home, work } = favorites
    const suggestsHomeWork = [
      home.label ? favorites.home : fakeFavorites.home,
      work.label ? favorites.work : fakeFavorites.work
    ]

    return [
      ...suggestsHomeWork.map(formatHomeOrWorkFavoriteForSuggest),
      ...others.map(other => formatFavoriteForSuggest(other))
    ]
  }
)

export const selectFavoritesSuggestions = createSelector(
  selectFavoritesFormattedForSuggest,
  selectSuggestInputValue,
  (favorites, inputValue) => {
    if (inputValue === '') return favorites.slice(0, 5)
    // TODO: vérifier avec le service que les favoris sont bien triés du plus récent au plus ancien

    return favorites.filter(favorite => {
      const inputValueKeyword = normalizeInputValueKeyword(inputValue)
      return isLabelMatchingInputValueKeyword(inputValueKeyword, favorite.labels.join(' '))
    })
  }
)

export const removeDuplicateSuggestions = (suggestionsToProcess, suggestionsToCompare) => {
  const labelsToCompare = suggestionsToCompare.map(suggestion => normalizeLabel((suggestion?.labels ?? []).join(' ')))
  return suggestionsToProcess.filter(suggestion => {
    const label = normalizeLabel(suggestion.labels.join(' '))
    return !labelsToCompare.find(item => item === label)
  })
}

export const selectAllSuggestions = (withoutFavorites = false) =>
  createSelector(
    selectSuggestError,
    withoutFavorites ? () => [] : selectFavoritesSuggestions,
    selectSearchHistorySuggestions,
    selectServiceSuggestions,
    (error, favoritesSuggestions, searchHistorySuggestions, serviceSuggestions) => {
      const favoritesSuggestionsWithUuid = favoritesSuggestions.filter(({ uuid }) => Boolean(uuid))
      const searchHistory = removeDuplicateSuggestions(searchHistorySuggestions, favoritesSuggestionsWithUuid)
      const service = removeDuplicateSuggestions(serviceSuggestions, [
        ...favoritesSuggestionsWithUuid,
        ...searchHistory
      ])

      return {
        error,
        suggestions: {
          favorites: favoritesSuggestionsWithUuid,
          searchHistory,
          service
        }
      }
    }
  )

export const selectHomeWorkOrFakeFavorites = createSelector(
  selectIsUserAccountEnabled,
  selectUserFavorites,
  selectSuggestFakeFavorites,
  (isUserAccountAvailable, favorites, fakeFavorites) => {
    if (!isUserAccountAvailable) return []
    const { home, work } = favorites
    return [
      home?.label ? formatHomeOrWorkFavoriteForSuggest(favorites.home) : fakeFavorites.home,
      work?.label ? formatHomeOrWorkFavoriteForSuggest(favorites.work) : fakeFavorites.work
    ]
  }
)
